/**
 * Pure helpers for WagePro two-step verification — sign-in code step,
 * sign-in enrolment step, and Settings › Security. No React, no I/O
 * (`wagepro_login_logic.ts` convention).
 *
 * Server side: `apps/api/src/trpc/routers/wagepro_two_factor.ts`.
 */

/** Length of an authenticator-app code. */
export const AUTHENTICATOR_CODE_LENGTH = 6;

/** Length of a recovery code (`generateBackupCodes()` in `apps/api/src/lib/totp.ts`). */
export const RECOVERY_CODE_LENGTH = 10;

/**
 * What `wageproAuth.login` resolved to, narrowed to what the login screen
 * does next. Mirrors the server's `WageproLoginResult`.
 */
export type WageproLoginOutcome =
  | { kind: 'employer' | 'employee'; native_session_token?: string }
  | { kind: 'two_factor_required'; pending_token: string }
  | {
      kind: 'two_factor_setup_required';
      setup_token: string;
      binding_secret: string;
    };

export type LoginNextStep =
  | { kind: 'signed_in'; native_session_token?: string }
  | { kind: 'enter_code'; pending_token: string }
  | { kind: 'set_up'; setup_token: string; binding_secret: string };

/** Decide the login screen's next step from a `wageproAuth.login` result. */
export function nextLoginStep(outcome: WageproLoginOutcome): LoginNextStep {
  switch (outcome.kind) {
    case 'two_factor_required':
      return { kind: 'enter_code', pending_token: outcome.pending_token };
    case 'two_factor_setup_required':
      return {
        kind: 'set_up',
        setup_token: outcome.setup_token,
        binding_secret: outcome.binding_secret,
      };
    case 'employer':
    case 'employee':
      return outcome.native_session_token === undefined
        ? { kind: 'signed_in' }
        : {
            kind: 'signed_in',
            native_session_token: outcome.native_session_token,
          };
  }
}

/**
 * Normalise what the user typed: authenticator apps and printed recovery
 * codes are often copied with spaces or a hyphen in the middle, and recovery
 * codes are case-insensitive.
 */
export function normaliseTwoFactorCode(raw: string): string {
  return raw.replace(/[\s-]/g, '').toUpperCase();
}

/** A six-digit authenticator code (already normalised). */
export function isAuthenticatorCode(code: string): boolean {
  return /^\d{6}$/.test(code);
}

/** A ten-character recovery code (already normalised). */
export function isRecoveryCode(code: string): boolean {
  return /^[A-Z0-9]{10}$/.test(code);
}

/**
 * Whether a sign-in code field holds something worth submitting: an
 * authenticator code, or — when the user switched to "use a recovery code" —
 * a recovery code.
 */
export function isSubmittableCode(
  code: string,
  mode: 'authenticator' | 'recovery',
): boolean {
  const normalised = normaliseTwoFactorCode(code);
  return mode === 'authenticator'
    ? isAuthenticatorCode(normalised)
    : isRecoveryCode(normalised);
}

/**
 * Show a recovery code in two readable halves (`ABCDE-12345`). The hyphen is
 * presentation only — {@link normaliseTwoFactorCode} strips it on the way back.
 */
export function formatRecoveryCode(code: string): string {
  if (code.length !== RECOVERY_CODE_LENGTH) return code;
  return `${code.slice(0, 5)}-${code.slice(5)}`;
}

/** The plain-text block copied to the clipboard / saved by the user. */
export function recoveryCodesAsText(codes: readonly string[]): string {
  return codes.map(formatRecoveryCode).join('\n');
}

/**
 * Split the manual-entry secret into groups of four for reading aloud or
 * typing into an authenticator app (`ABCD EFGH …`).
 */
export function groupSecret(secret: string): string {
  return secret.replace(/(.{4})(?=.)/g, '$1 ');
}

/**
 * The failure cases the two-step screens explain in their own translated
 * words. The server's English messages are matched here, in one place, so
 * the screens never render a raw server string.
 */
export type TwoFactorErrorKind =
  | 'invalid_code'
  | 'expired'
  | 'too_many_attempts'
  | 'wrong_password'
  | 'required_by_company'
  | 'enable_self_first'
  | 'network'
  | 'unknown';

function readString(err: object, key: string): string | undefined {
  const value = key in err ? (err as Record<string, unknown>)[key] : undefined;
  return typeof value === 'string' ? value : undefined;
}

function readErrorCode(err: object): string | undefined {
  const data = 'data' in err ? (err as { data?: unknown }).data : undefined;
  if (typeof data !== 'object' || data === null) return undefined;
  return readString(data, 'code');
}

const NETWORK_FAILURE_MESSAGE =
  /failed to fetch|load failed|networkerror|network request failed|fetch failed/i;

export function classifyTwoFactorError(err: unknown): TwoFactorErrorKind {
  if (typeof err !== 'object' || err === null) return 'unknown';
  const message = readString(err, 'message') ?? '';
  const code = readErrorCode(err);

  if (code === 'TOO_MANY_REQUESTS') return 'too_many_attempts';
  if (code === 'UNAUTHORIZED' && /invalid credentials/i.test(message)) {
    return 'wrong_password';
  }
  if (code === 'FORBIDDEN' && /company requires/i.test(message)) {
    return 'required_by_company';
  }
  if (code === 'BAD_REQUEST') {
    if (/^invalid code$/i.test(message)) return 'invalid_code';
    if (/expired session|sign in again|start setup again/i.test(message)) {
      return 'expired';
    }
    if (/your own account first/i.test(message)) return 'enable_self_first';
  }
  if (code === undefined) {
    if (err instanceof TypeError || NETWORK_FAILURE_MESSAGE.test(message)) {
      return 'network';
    }
  }
  return 'unknown';
}
